import { useLingui } from '@lingui/react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Endpoints, ProductData } from '@/api';
import { I18nWording, Locale } from '@/services/i18n';
import { useFetch } from '@/utils/fetch';
import { sortAlphabetically } from '@/utils/sort';

import type { ProductAttributeValue } from './productAttribute';

export const getExternalIdNameCollection = (product: {
  externalId: string;
  name: string;
  data: ProductData;
}) =>
  product.externalId +
  (product.name !== product.externalId ? ` • ${product.name}` : '') +
  (product.data?.collection ? ` (${product.data.collection})` : '');

export type ColorWithImage = { color: I18nWording; image?: string };

type GetColorOptionsParams = {
  article: {
    product?: {
      colorsWithImage?: ColorWithImage[] | null;
    } | null;
    productAttributeValues?: ProductAttributeValue[];
    snapshot: {
      articleExchange?: {
        replacementProduct?: {
          colorsWithImage?: ColorWithImage[] | null;
        } | null;
      } | null;
    };
  };
  isReplacementProduct?: boolean;
};

const getColorOptions = ({ article, isReplacementProduct }: GetColorOptionsParams) => {
  if (isReplacementProduct) {
    return (
      article.snapshot.articleExchange?.replacementProduct?.colorsWithImage?.map(
        (colorWithImage) => colorWithImage.color
      ) ?? []
    );
  }

  const productAttributeValues = article.productAttributeValues ?? [];

  const colorsForSelectedProductAttributes = productAttributeValues
    .reverse()
    .find((productAttributeValue) => !!productAttributeValue.colors?.length)?.colors;

  return (
    article.product?.colorsWithImage?.map((colorWithImage) => colorWithImage.color) ??
    colorsForSelectedProductAttributes ??
    []
  );
};

export const hasColorOptions = (params: GetColorOptionsParams) => {
  return !!getColorOptions(params).length;
};

export const useColorOptions = (params: GetColorOptionsParams) => {
  const { i18n } = useLingui();

  return sortAlphabetically(getColorOptions(params), i18n.locale as Locale);
};

type ProductAttributeFromValue = {
  id: string;
  name: I18nWording;
  parentProductAttributeValueId: string | null;
  sortOrder: number;
};
type ProductAttributeValueFromProduct = {
  id: string;
  name: I18nWording;
  productAttribute: {
    id: string;
    name: I18nWording;
    parentProductAttributeValueId: string | null;
  };
};

export type ProductAttributeTreeNode = {
  attribute: ProductAttributeFromValue;
  values: ProductAttributeTreeValueNode[];
};

export type ProductAttributeTreeValueNode = {
  value: ProductAttributeValueFromProduct;
  childAttributes: ProductAttributeTreeNode[];
};

export type ProductAttributeValuesForTree = {
  id: string;
  name: I18nWording;
  sortOrder: number | null;
  productAttribute: {
    id: string;
    name: I18nWording;
    parentProductAttributeValueId: string | null;
    sortOrder: number;
  };
}[];

export const getProductAttributesTree = (
  productAttributeValues: ProductAttributeValuesForTree
): ProductAttributeTreeNode[] => {
  const buildTree = (parentValueId: string | null): ProductAttributeTreeNode[] => {
    const attributesById = new Map<string, ProductAttributeFromValue>();

    for (const productAttributeValue of productAttributeValues) {
      const { productAttribute } = productAttributeValue;

      if (productAttribute.parentProductAttributeValueId !== parentValueId) {
        continue;
      }

      attributesById.set(productAttribute.id, productAttribute);
    }

    return [...attributesById.values()]
      .toSorted((a, b) => a.sortOrder - b.sortOrder)
      .map((attribute) => ({
        attribute,
        values: productAttributeValues
          .filter(
            (productAttributeValue) => productAttributeValue.productAttribute.id === attribute.id
          )
          .toSorted((a, b) => {
            if (a.sortOrder === b.sortOrder) {
              return 0;
            }

            if (a.sortOrder === null) {
              return 1;
            }

            if (b.sortOrder === null) {
              return -1;
            }

            return a.sortOrder - b.sortOrder;
          })
          .map((value) => ({
            value,
            childAttributes: buildTree(value.id),
          })),
      }));
  };

  return buildTree(null);
};

export type Product = Endpoints['GET /products/:id']['response'];

export const useProduct = (id?: string) => {
  const fetch = useFetch<Endpoints['GET /products/:id']>();

  return useQuery({
    queryKey: ['products', id],
    queryFn: () => fetch(`/products/${id}`),
    enabled: !!id,
  });
};

export type ProductFromGetMany = Endpoints['GET /products']['response']['products'][number];

type ListProductsQuery = Endpoints['GET /products']['query'];

export const useProducts = (
  params: ListProductsQuery,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /products']>();

  return useQuery({
    queryKey: ['products', params],
    queryFn: () => fetch('/products', params),
    enabled: options?.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useCreateProduct = (requestId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /products']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /products']['body']) =>
      fetch(`/products`, undefined, {
        method: 'POST',
        body,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['requests', requestId] }),
  });
};

export const useUpdateProduct = (productId?: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /products/:id']>();

  return useMutation({
    mutationFn: (body: Endpoints['PATCH /products/:id']['body']) =>
      fetch(`/products/${productId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['products', productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
};
